import React, { useState, useEffect, useRef, useMemo } from 'react';
import './AdvancedSearchPopup.css';

function AdvancedSearchPopup({ allFileNames, initiallyActiveFiles, onApply, onClose, onRefreshRequest, context, lang }) {
  
  // Normalize allFileNames to array of { file, name, name_km }
  const normalizedFiles = useMemo(() => {
    if (!Array.isArray(allFileNames)) return [];
    return allFileNames.map(item => {
      if (!item) return null;
      if (typeof item === 'string') return { file: item, name: item, name_km: item };
      return { file: item.file ?? item.name ?? String(item), name: item.name ?? item.file ?? String(item), name_km: item.name_km };
    }).filter(Boolean);
  }, [allFileNames]);

  // show all files, prioritize Khmer name if available
  const khmerFiles = useMemo(() => normalizedFiles, [normalizedFiles]);

  // Initialize checked files with whatever was passed from the parent
  const [checkedFiles, setCheckedFiles] = useState(() => {
    return new Set(Array.from(initiallyActiveFiles || []));
  });
  
  // Also ensure the correct files are checked when the component remounts
  useEffect(() => {
    setCheckedFiles(new Set(Array.from(initiallyActiveFiles || [])));
  }, [initiallyActiveFiles, context]);
  
  const selectAllCheckboxRef = useRef(null);
  
  // Fix the indeterminate state of the Select All checkbox (use khmerFiles)
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const numChecked = checkedFiles.size;
      const numTotal = khmerFiles.length;
      selectAllCheckboxRef.current.indeterminate = numTotal > 0 && numChecked > 0 && numChecked < numTotal;
    }
  }, [checkedFiles, khmerFiles]);
  
  const handleCheckboxChange = (fileName) => {
    console.log(`AdvancedSearchPopup (${context}): Checkbox change for:`, fileName);
    setCheckedFiles(prev => {
      const newChecked = new Set(prev);
      if (newChecked.has(fileName)) {
        newChecked.delete(fileName);
      } else {
        newChecked.add(fileName);
      }
      return newChecked;
    });
  };
  
  const handleSelectAll = () => {
    // include only Khmer-file keys
    const allFiles = new Set(khmerFiles.map(f => f.file));
    setCheckedFiles(allFiles);
  };

  const handleDeselectAll = () => {
    setCheckedFiles(new Set());
  };

  const handleSubmit = () => {
    console.log(`AdvancedSearchPopup (${context}): Submitting checked files:`, Array.from(checkedFiles));
    // Pass checked files to parent component
    onApply(checkedFiles);
    onClose();
  };

  // determine effective language: force Khmer-only UI
  const effectiveLang = 'km';

  // simple translations (force Khmer)
  const t = {
    title: 'ការជម្រើសរើសច្បាប់',
    prompt: 'សូមជ្រើសច្បាប់ណាមួយដែលអ្នកចង់ស្វែងរក៖',
    selectAll: 'ជ្រើសរើសច្បាប់ទាំងអស់',
    noFiles: 'អ្នកពុំបានជ្រើសរើសច្បាប់ណាមួយសម្រាប់ស្វែងរក',
    apply: 'អនុវត្ត',
    cancel: 'បោះបង់'
  };

  // Special rendering function for checkboxes — use name_km only
  const renderCheckbox = (fileObject) => {
    const fileName = typeof fileObject === 'string' ? fileObject : (fileObject.file ?? fileObject.name);
    
    // Get the base label
    let rawLabel = (typeof fileObject === 'string'
      ? fileObject
      : (fileObject.name_km || fileObject.name || 'Unknown'));

    // Manual Override: If the years are missing for specific files, append them manually.
    if (fileName === 'CivilCodeExplain.txt' && !rawLabel.includes('២០០៩')) {
         rawLabel += ', ២០០៩';
    } else if (fileName === 'CivilProExplain.txt' && !rawLabel.includes('២០០៧')) {
         rawLabel += ', ២០០៧';
    }

    // Bold the entire label
    let label = <strong>{rawLabel}</strong>;

    const isChecked = checkedFiles.has(fileName);

    return (
      <div key={fileName} className="checkbox-item">
        <input
          type="checkbox"
          id={`cb-${fileName}`}
          checked={Boolean(isChecked)}
          onChange={() => handleCheckboxChange(fileName)}
        />
        <label htmlFor={`cb-${fileName}`}>{label}</label>
      </div>
    );
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <h2>{t.title}</h2>
        <p>{t.prompt}</p>
        
        <div className="popup-actions-top">
          <label className="checkbox-item">
            <input
              type="checkbox"
              ref={selectAllCheckboxRef}
              checked={checkedFiles.size > 0 && khmerFiles.length > 0 && checkedFiles.size === khmerFiles.length}
              onChange={e => {
                if (e.target.checked) {
                  handleSelectAll();
                } else {
                  handleDeselectAll();
                }
              }}
            />
            {t.selectAll}
          </label>
        </div>
        
        <div className="file-checkbox-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
          {khmerFiles.length > 0 ? (
            khmerFiles.map(fileObject => renderCheckbox(fileObject))
          ) : (
            <p>{t.noFiles}</p>
          )}
        </div>
        
        <div className="popup-actions-bottom">
          <button
            onClick={handleSubmit}
            className="popup-button primary"
            disabled={checkedFiles.size === 0}
            aria-disabled={checkedFiles.size === 0}
            title={checkedFiles.size === 0 ? 'សូមជ្រើសច្បាប់មួយយ៉ាងតិច' : ''}
          >
            {t.apply}
          </button>
          <button onClick={onClose} className="popup-button secondary">{t.cancel}</button>
        </div>
      </div>
    </div>
  );
}

export default AdvancedSearchPopup;